import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';

import { restoreSession } from './lib/auth/authenticatedFetch';
import { router } from './routes/router';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The public payload is served with a 60-second origin cache, so matching that
      // avoids a refetch the API would answer from cache anyway.
      staleTime: 60_000,

      // One retry helps a visitor on a flaky mobile connection; more than that just
      // delays the error state.
      retry: 1,

      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  // A ref, not state: this guards a one-time side effect and nothing renders from it.
  // Setting state synchronously in an effect triggers a cascading render for no reason.
  // StrictMode double-invokes effects in development, so the guard is required.
  const restoreStarted = useRef(false);

  useEffect(() => {
    if (restoreStarted.current) {
      return;
    }

    restoreStarted.current = true;

    // Attempts to exchange the httpOnly refresh cookie for a session, once, on boot.
    //
    // The access token lives only in memory, so a page reload always loses it and only
    // the cookie can bring the session back. Without this the owner would be signed out
    // by every refresh. It runs for anonymous visitors too and simply fails, which is why
    // restoreSession swallows the error rather than surfacing one.
    void restoreSession();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
